<template>
<div
  class="product-quantity-controls-component"
  :class="{
    'being-added-to-cart': productIsAddingToCart,
    'being-removed-from-cart': productIsRemovingFromCart,
    'in-cart': isInCart,
    'measured-controls': quantityEditDisabled,
    'always-visible': productsSettings.cart_button_visibility === 'always',
  }"
>
  <!-- If you're chaning transition name, make sure to also change it in
        `products.less` – `.fade-enter-active`. -->
  <transition name="fade">
    <div v-if="isInCart" class="product-quantity-controls-wrapper" key="inCart">
      <div class="product-quantity-control-column product-quantity-minus">
        <button type="button"
                class="btn btn-block"
                @click="decreasePendingQuantity"
                :disabled="orderActionsAreBlocked"
                :class="buttonClasses"
        >
          <i class="fal" :class="{ 'fa-minus': !productIsRemovingFromCart, 'fa-spinner fa-spin': productIsRemovingFromCart }"></i>
        </button>
      </div>
      <!-- TODO roll down to up if the quantity is decreased? -->
      <div class="product-quantity-control-column product-quantity-number-wrapper">
        <transition name="roll">
          <input v-if="quantityEditDisabled"
                 type="text"
                 tabindex="-1"
                 :readonly="true"
                 :key="cartQuantity"
                 :value="readOnlyQuantityLabel"
                 @keydown.enter="replaceQuantity"
                 @blur="replaceQuantity"
          />
          <input v-else
                 type="text"
                 tabindex="-1"
                 :key="cartQuantity"
                 v-model="currentQuantity"
                 @keydown.enter="replaceQuantity"
                 @blur="replaceQuantity"
          />
        </transition>
      </div>
      <div class="product-quantity-control-column product-quantity-plus">
        <button type="button" class="btn btn-block" @click="increasePendingQuantity" :disabled="!canAddToCart" :class="buttonClasses">
          <i class="fal" :class="{ 'fa-plus': !productIsAddingToCart, 'fa-spinner fa-spin': productIsAddingToCart }"></i>
        </button>
      </div>
    </div>
    <div v-else class="product-cart-controls-wrapper" key="notInCart">
      <button v-if="cartButtonDisabled" type="button" class="btn btn-primary btn-block btn-rounded btn-lg" disabled>
        {{ productsSettings.cart_button_available }}
      </button>
      <DistortedButton v-else-if="isInStock"
                       type="button"
                       @click="increasePendingQuantity"
                       :disabled="!canAddToCart"
      >
        <i class="fal fa-spinner fa-spin" v-if="productIsRemovingFromCart || productIsAddingToCart"></i>&nbsp;
        {{ productsSettings.cart_button_available }}
      </DistortedButton>
      <button v-else type="button" class="btn btn-primary btn-block btn-rounded btn-lg" disabled>
        {{ productsSettings.cart_button_unavailable }}
      </button>
    </div>
  </transition>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import DistortedButton from './DistortedButton'
import zenkyEvents from '@/events'

export default {
  props: {
    product: {
      required: true,
      type: Object,
    },

    selectedVariant: {
      required: true,
      type: Object,
    },

    isInStock: {
      required: false,
      type: Boolean,
      default: false,
    },

    small: {
      required: false,
      type: Boolean,
      default: false,
    },

    modifiers: {
      required: false,
      type: Array,
      default: () => [],
    },

    modifiersHash: {
      required: false,
      type: String,
      default: '',
    },

    promotionId: {
      required: false,
      type: String,
      default: '',
    },

    promotionRewardId: {
      required: false,
      type: String,
      default: '',
    },

    isPromocodeReward: {
      required: false,
      type: Boolean,
      default: false,
    },

    stockId: {
      required: false,
      type: String,
      default: null,
    },

    cartButtonDisabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  components: { DistortedButton },

  computed: {
    ...mapGetters({
      productsSettings: 'settings/products',
      hasOrder: 'order/hasOrder',
      hasOrderVariants: 'order/hasVariants',
      orderVariants: 'order/variants',
    }),

    buttonClasses() {
      if (this.small) {
        return ['btn-sm']
      }

      return ['btn-lg']
    },

    isInCart() {
      if (!this.hasOrderVariants) {
        return false
      }

      return -1 !== this.orderVariants.findIndex(ov => this.isTheSameVariant(ov))
    },

    cartQuantity() {
      if (!this.isInCart) {
        return 0
      }

      const variant = this.orderVariants.find(ov => this.isTheSameVariant(ov));

      if (this.quantityEditDisabled) {
        return variant.measured_quantity;
      }

      return variant.quantity;
    },

    orderActionsAreBlocked() {
      return this.productIsAddingToCart || this.productIsRemovingFromCart
    },

    canAddToCart() {
      return this.isInStock && !this.orderActionsAreBlocked
    },

    quantityStep() {
      return this.product.quantity_step || 1.0;
    },

    quantityEditDisabled() {
      return this.quantityStep !== 1.0;
    },

    readOnlyQuantityLabel() {
      return `${this.currentQuantity} ${this.product.unit_type.short_name}`
    },
  },

  methods: {
    ...mapActions({
      addProductVariant: 'order/addProductVariant',
      removeProductVariant: 'order/removeProductVariant',
    }),

    isTheSameVariant(orderVariant) {
      if (orderVariant.product_variant_id !== this.selectedVariant.id || orderVariant.modifiers_hash !== this.modifiersHash) {
        return false
      }

      const isPromocodeReward = this.isPromocodeReward === orderVariant.is_promocode_reward;

      if (!this.promotionId || !this.promotionRewardId) {
        return isPromocodeReward && orderVariant.promotion_reward_id === null
      }

      return isPromocodeReward
        && orderVariant.promotion_id === this.promotionId
        && orderVariant.promotion_reward_id === this.promotionRewardId
    },

    increasePendingQuantity() {
      this.pendingQuantity++;
      const newQuantity = this.currentQuantity + this.quantityStep;
      this.currentQuantity = Math.round(newQuantity * 10) / 10;
    },

    decreasePendingQuantity() {
      this.pendingQuantity--;
      const newQuantity = this.currentQuantity - this.quantityStep;
      this.currentQuantity = Math.max(0, Math.round(newQuantity * 10) / 10);
    },

    addToCart(quantity = 1) {
      if (!this.canAddToCart) {
        return
      }

      this.productIsAddingToCart = true

      if (quantity === undefined) {
        quantity = 1
      }

      const payload = {
        quantity,
        variantId: this.selectedVariant.id,
        modifiers: this.modifiers,
        promotion_id: this.promotionId || null,
        promotion_reward_id: this.promotionRewardId || null,
      }

      if (this.stockId) {
        payload.stock_id = this.stockId
      }

      this.addProductVariant(payload)
        .then(() => {
          this.productIsAddingToCart = false

          this.$root.sharedEventBus.$emit(zenkyEvents.PRODUCT_ADDED_TO_ORDER, {
            quantity,
            product: this.product,
            variant: this.selectedVariant,
          })
        })
        .catch(err => {
          this.productIsAddingToCart = false
          this.currentQuantity = this.cartQuantity
          console.error(err)
        })
    },

    replaceQuantity() {
      const currentQuantity = this.cartQuantity
      const newQuantity = this.currentQuantity

      if (currentQuantity > newQuantity) {
        return this.removeFromCart(currentQuantity - newQuantity)
      } else if (newQuantity > currentQuantity) {
        return this.addToCart(newQuantity - currentQuantity)
      }
    },

    removeFromCart(quantity = 1) {
      if (this.productIsRemovingFromCart) {
        return
      }

      this.productIsRemovingFromCart = true

      if (quantity === undefined) {
        quantity = 1
      }

      const payload = {
        quantity,
        variantId: this.selectedVariant.id,
        modifiers: this.modifiers,
        promotion_id: this.promotionId || null,
        promotion_reward_id: this.promotionRewardId || null,
      }

      if (this.stockId) {
        payload.stock_id = this.stockId
      }

      this.removeProductVariant(payload)
        .then(() => {
          this.productIsRemovingFromCart = false

          this.$root.sharedEventBus.$emit(zenkyEvents.PRODUCT_REMOVED_FROM_ORDER, {
            quantity,
            product: this.product,
            variant: this.selectedVariant,
          })
        })
        .catch(err => {
          this.productIsRemovingFromCart = false
          this.currentQuantity = this.cartQuantity
          console.error(err)
        })
    },

    applyPendingQuantity() {
      if (this.pendingQuantity > 0) {
        this.addToCart(this.pendingQuantity)
      } else if (this.pendingQuantity < 0) {
        this.removeFromCart(Math.abs(this.pendingQuantity))
      }

      this.pendingQuantity = 0
    },
  },

  watch: {
    cartQuantity: {
      immediate: true,
      handler(val) {
        this.currentQuantity = val
      },
    },

    pendingQuantity() {
      clearTimeout(this.pendingQuantityTimeout)

      this.pendingQuantityTimeout = setTimeout(() => {
        this.applyPendingQuantity()
      }, 300)
    },
  },

  data() {
    return {
      productIsAddingToCart: false,
      productIsRemovingFromCart: false,
      currentQuantity: 0,
      pendingQuantity: 0,
      pendingQuantityTimeout: 0,
    }
  }
}
</script>

<style lang="less">
.product-quantity-controls-component {
  @apply flex relative;

  &.measured-controls {
    @apply w-32 !important;
  }

  .product-quantity-controls-wrapper {
    @apply w-full flex rounded;

    .product-quantity-control-column {
      @apply flex-1;

      > * {
        @apply h-full;
      }
      &.product-quantity-number-wrapper {
        @apply relative;
        input {
          @apply absolute inset-0; // For the `in-out` transition.
          @apply w-full text-center outline-none bg-transparent;

          &:focus {
            @apply outline-none;
          }
        }
      }
    }
  }

  .product-cart-controls-wrapper {
    @apply w-full flex;
    .distorted-button {
      @apply rounded;
    }
  }

  .fade-leave-active {
    @apply absolute;
  }

  .fade-enter,
  .fade-leave-to {
    transform: scale(1.1) translateY(5px);
  }
}
</style>
